import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ActivityIndicator,
    FlatList,
    StyleSheet,
    TouchableOpacity,
    View,
    useWindowDimensions,
} from "react-native";
import { Card, Checkbox } from "react-native-paper";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../../styles/commonStyles";
import CustomLabel from "../../../../components/CustomLabel/CustomLabel";
import CustomInput from "../../../../components/CustomInput";
import CustomButton from "../../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { useCompany } from "../../../../hooks/useCompany";
import { showMessage } from "react-native-flash-message";
import Swal from "sweetalert2";
import { v4 as uuidv4 } from "uuid";

type RateItem = {
    rateID: string;
    company: string;
    description?: string;
    defRate: boolean;
    userid?: string;
    doc?: string;
    rowguid?: string;
    __tempKey?: string;
    __isNew?: boolean;
    __originalRateID?: string;
    __originalCompany?: string;
    __markedForDelete?: boolean;
};

type RatesScreenProps = {
    selectedCompany?: string;
};

type RateRowProps = {
    item: RateItem;
    index: number;
    styles: ReturnType<typeof makeStyles>;
    handleEditPress: (index: number, item: RateItem, key: keyof RateItem, value: string | boolean) => void;
    handleDelete: (item: RateItem, index: number) => void;
    isChanged: boolean;
    isDeleted: boolean;
};

const RatesListRow = React.memo(
    ({ item, index, styles, handleEditPress, handleDelete, isChanged, isDeleted }: RateRowProps) => {
        return (
            <View
                style={[
                    styles.rowContainer,
                    /* todo: add later if also added in other pages isChanged && styles.changedRow,*/
                    isDeleted && styles.deletedRow,
                ]}
            >
                <View style={[styles.row, isDeleted && styles.disabledRowContent]}>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.rateID ?? ""}
                            setValue={(text: string) => handleEditPress(index, item, "rateID", text)}
                            editable={!isDeleted}
                            maxLen={1}
                            type="MAININPUT"
                        />
                    </View>

                    <View style={styles.item}>
                        <CustomInput
                            value={item.description ?? ""}
                            setValue={(text: string) => handleEditPress(index, item, "description", text)}
                            editable={!isDeleted}
                            maxLen={50}
                            type="MAININPUT"
                        />
                    </View>

                    <View style={styles.checkboxContainer}>
                        <Checkbox
                            status={item.defRate ? "checked" : "unchecked"}
                            onPress={() => !isDeleted && handleEditPress(index, item, "defRate", !item.defRate)}
                            color={item.defRate ? "#86BC25" : undefined}
                            disabled={isDeleted}
                        />
                    </View>

                    <View style={styles.actionContainer}>
                        <CustomButton
                            iconName={isDeleted ? "refresh-outline" : "trash-outline"}
                            onPress={() => handleDelete(item, index)}
                            show="HOME_BELL"
                            type="HOME_BELL"
                            textTop="2"
                            sz={30}
                        />
                    </View>
                </View>
            </View>
        );
    }
);

const HRAdminRatesScreen: React.FC<RatesScreenProps> = (props) => {
    const [rates, setRates] = useState<RateItem[]>([]);
    const [originalRates, setOriginalRates] = useState<RateItem[]>([]);
    const [changes, setChanges] = useState<Record<string, RateItem>>({});
    const [deletedItems, setDeletedItems] = useState<RateItem[]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const ref = useRef<FlatList<RateItem>>(null);

    const { t } = useTranslation();
    const { colors } = useTheme();
    const { width, height } = useWindowDimensions();
    const styles = useMemo(() => makeStyles(colors, width, height), [colors, width, height]);
    const common = createCommonStyles(colors);

    const { selectedCompany } = props;
    const {
        getStandardRates,
        postStandardRates,
        putStandardRates,
        deleteStandardRates,
    } = useCompany();

    const getRowKey = useCallback((item: RateItem): string => {
        return item.rowguid || item.__tempKey || `${item.company}-${item.rateID}`;
    }, []);

    const scrollToTop = () => {
        ref.current?.scrollToOffset({ offset: 0, animated: true });
    };

    const loadRates = useCallback(async (silent = false) => {
        if (!selectedCompany) {
            setRates([]);
            setOriginalRates([]);
            setChanges({});
            setDeletedItems([]);
            setIsInitialLoading(false);
            return;
        }

        if (!silent) {
            setIsInitialLoading(true);
        }

        try {
            const fetchedRates: RateItem[] = await getStandardRates(selectedCompany);

            const normalizedRates = (fetchedRates || []).map((item) => ({
                ...item,
                rateID: item.rateID ?? "",
                company: item.company ?? selectedCompany,
                description: item.description ?? "",
                defRate: Boolean(item.defRate),
                __originalRateID: item.rateID,
                __originalCompany: item.company,
                __isNew: false,
                __markedForDelete: false,
            }));

            setRates(normalizedRates);
            setOriginalRates(normalizedRates);
            setChanges({});
            setDeletedItems([]);
        } catch (error) {
            showMessage({
                message: t("common:errorLoadingData"),
                type: "danger",
                duration: 3000,
            });
            setRates([]);
            setOriginalRates([]);
        } finally {
            setIsInitialLoading(false);
        }
    }, [selectedCompany]);

    useEffect(() => {
        loadRates(false);
    }, [selectedCompany]);

    const removeFromChangesAfterSave = () => {
        setChanges({});
        setDeletedItems([]);
    };

    const isRowChanged = useCallback(
        (item: RateItem) => {
            const rowKey = getRowKey(item);
            return Boolean(changes[rowKey]) && !item.__markedForDelete;
        },
        [changes, getRowKey]
    );

    const isRowDeleted = useCallback((item: RateItem) => {
        return Boolean(item.__markedForDelete);
    }, []);

    const addNewRate = () => {
        if (!selectedCompany) {
            showMessage({
                message: t("common:selectCompanyFirst"),
                type: "info",
                duration: 3000,
            });
            return;
        }

        const hasEmptyNewRow = rates.some(
            (row) =>
                row.__isNew &&
                !row.rateID?.trim() &&
                !row.description?.trim() &&
                row.defRate === false
        );

        if (hasEmptyNewRow) {
            showMessage({
                message: t("common:multEmptyLines"),
                type: "danger",
                duration: 3000,
            });
            return;
        }

        const tempKey = uuidv4();

        const newRow: RateItem = {
            rateID: "",
            company: selectedCompany,
            description: "",
            defRate: false,
            __tempKey: tempKey,
            __isNew: true,
            __originalRateID: "",
            __originalCompany: selectedCompany,
            __markedForDelete: false,
        };

        setRates((prev) => [newRow, ...prev]);
        setChanges((prev) => ({
            ...prev,
            [tempKey]: newRow,
        }));

        scrollToTop();
    };

    const handleEditPress = useCallback(
        (index: number, item: RateItem, key: keyof RateItem, value: string | boolean) => {
            if (item.__markedForDelete) return;

            let sanitizedValue: string | boolean = value;

            if (key === "rateID" && typeof value === "string") {
                sanitizedValue = value.toUpperCase().replace(/\s/g, "").slice(0, 1);
            }

            if (key === "description" && typeof value === "string") {
                sanitizedValue = value.slice(0, 50);
            }

            if (item[key] === sanitizedValue) return;

            const rowKey = getRowKey(item);

            if (key === "defRate" && sanitizedValue === true) {
                setRates((prev) =>
                    prev.map((row, i) => {
                        if (row.__markedForDelete) return row;

                        if (i === index) {
                            return { ...row, defRate: true };
                        }

                        if (row.defRate) {
                            return { ...row, defRate: false };
                        }

                        return row;
                    })
                );

                setChanges((prev) => {
                    const next = { ...prev };

                    rates.forEach((row) => {
                        if (row.__markedForDelete) return;

                        const keyForRow = getRowKey(row);

                        if (keyForRow === rowKey) {
                            next[keyForRow] = {
                                ...(prev[keyForRow] ?? row),
                                defRate: true,
                            };
                        } else if (row.defRate) {
                            next[keyForRow] = {
                                ...(prev[keyForRow] ?? row),
                                defRate: false,
                            };
                        }
                    });

                    return next;
                });

                return;
            }

            setRates((prev) =>
                prev.map((row, i) =>
                    i === index
                        ? {
                            ...row,
                            [key]: sanitizedValue,
                        }
                        : row
                )
            );

            setChanges((prev) => ({
                ...prev,
                [rowKey]: {
                    ...(prev[rowKey] ?? item),
                    [key]: sanitizedValue,
                },
            }));
        },
        [getRowKey, rates]
    );

    const handleCancelChanges = () => {
        setRates(originalRates);
        setChanges({});
        setDeletedItems([]);
        showMessage({
            message: t("common:changesCancelled"),
            type: "info",
            duration: 3000,
        });
    };

    const validateInput = (): [boolean, string] => {
        const currentRows = rates.filter((row) => !row.__markedForDelete);

        for (const row of currentRows) {
            if (!row.company || row.company.trim() === "" || row.company.length > 5) {
                return [false, "Company is required and must be max 5 characters."];
            }

            if (!row.rateID || row.rateID.trim() === "" || row.rateID.length > 1) {
                return [false, "Rate ID is required and must be exactly 1 character."];
            }

            if ((row.description ?? "").length > 50) {
                return [false, "Description must be max 50 characters."];
            }
        }

        const defRateCount = currentRows.filter((row) => row.defRate).length;
        if (defRateCount > 1) {
            return [false, "Only one rate can be marked as default."];
        }

        const seen = new Set<string>();
        for (const row of currentRows) {
            const key = `${row.company.toUpperCase()}-${row.rateID.toUpperCase()}`;
            if (seen.has(key)) {
                return [false, "Duplicate Rate ID found for the selected company."];
            }
            seen.add(key);
        }

        return [true, ""];
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);

            const [isValid, validationMessage] = validateInput();
            if (!isValid) {
                showMessage({
                    message: validationMessage,
                    type: "danger",
                    duration: 4000,
                });
                setIsSaving(false);
                return;
            }

            if (deletedItems.length > 0) {
                for (const item of deletedItems) {
                    if (!item.rowguid) continue;

                    const deleteResult = await deleteStandardRates({
                        Company: item.company,
                        RateId: item.rateID,
                    });

                    if (deleteResult && deleteResult.success === false) {
                        if (deleteResult.conflict) {
                            const detailMessage =
                                deleteResult.details?.detail || t("common:cannotDeleteItem");
                            await Swal.fire({
                                title: t("common:cannotDelete"),
                                text: detailMessage,
                                icon: "error",
                                confirmButtonText: t("common:ok"),
                            });
                        } else {
                            showMessage({
                                message: t("common:failedDelete"),
                                type: "danger",
                                duration: 3000,
                            });
                        }
                        setIsSaving(false);
                        return;
                    }
                }

                showMessage({
                    message: t("common:successDelete"),
                    type: "success",
                    duration: 3000,
                });
            }

            const changedRows = Object.values(changes).filter((row) => !row.__markedForDelete);

            if (changedRows.length > 0) {
                for (const change of changedRows) {
                    const isExisting = Boolean(change.rowguid);

                    if (isExisting) {
                        await putStandardRates({
                            Company: change.company,
                            Description: change.description ?? "",
                            DefRate: Boolean(change.defRate),
                            RateId: change.rateID,
                            RowGuid: change.rowguid,
                        });
                    } else {
                        await postStandardRates({
                            Company: change.company,
                            Description: change.description ?? "",
                            DefRate: Boolean(change.defRate),
                            RateId: change.rateID,
                        });
                    }
                }

                showMessage({
                    message: t("common:successSave"),
                    type: "success",
                    duration: 3000,
                });
            }

            if (deletedItems.length === 0 && changedRows.length === 0) {
                showMessage({
                    message: t("common:noChanges"),
                    type: "info",
                    duration: 3000,
                });
            }

            removeFromChangesAfterSave();
            await loadRates(true);
        } catch (error: any) {
            const validationErrors = error?.response?.data?.errors || {};
            const messages: string[] = Object.values(validationErrors).flat() as string[];

            const duplicateError = messages.some((msg) => {
                const lower = msg.toLowerCase();
                return (
                    lower.includes("duplicate") ||
                    lower.includes("already exists") ||
                    lower.includes("unique constraint")
                );
            });

            if (duplicateError) {
                showMessage({
                    message: "Duplicate Rate ID for this company.",
                    type: "danger",
                    duration: 5000,
                });
            } else {
                showMessage({
                    message: t("common:errorSave"),
                    type: "danger",
                    duration: 3000,
                });
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (item: RateItem, index: number) => {
        if (!item) {
            showMessage({
                message: t("common:invalidItem"),
                type: "danger",
                duration: 3000,
            });
            return;
        }

        const rowKey = getRowKey(item);

        if (!item.rowguid) {
            setRates((prev) => prev.filter((_, i) => i !== index));
            setChanges((prev) => {
                const next = { ...prev };
                delete next[rowKey];
                return next;
            });

            showMessage({
                message: t("common:rowDeletedLocally"),
                type: "success",
                duration: 2000,
            });
            return;
        }

        setRates((prev) =>
            prev.map((row, i) =>
                i === index
                    ? {
                        ...row,
                        __markedForDelete: !row.__markedForDelete,
                    }
                    : row
            )
        );

        setDeletedItems((prev) => {
            const alreadyDeleted = prev.some((x) => getRowKey(x) === rowKey);

            if (alreadyDeleted) {
                return prev.filter((x) => getRowKey(x) !== rowKey);
            }

            return [...prev, { ...item, __markedForDelete: true }];
        });

        setChanges((prev) => {
            const next = { ...prev };
            delete next[rowKey];
            return next;
        });
    };

    const renderData = useCallback(
        (item: RateItem, index: number) => (
            <Card style={[common.cardstyle]}>
                <Card.Content>
                    <RatesListRow
                        item={item}
                        index={index}
                        styles={styles}
                        handleEditPress={handleEditPress}
                        handleDelete={handleDelete}
                        isChanged={isRowChanged(item)}
                        isDeleted={isRowDeleted(item)}
                    />
                </Card.Content>
            </Card>
        ),
        [common.cardstyle, styles, handleEditPress, handleDelete, isRowChanged, isRowDeleted]
    );

    if (isInitialLoading) {
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        );
    }

    return (
        <View style={styles.mainView}>
            <View style={styles.container}>
                <View style={styles.buttonsRow}>
                    <View style={styles.leftButtons} />
                    <View style={styles.rightButtons}>
                        <CustomButton
                            text={t("common:new")}
                            onPress={addNewRate}
                            type="BUTTON_ADD"
                            textTop="0"
                            sz={15}
                        />

                        {(Object.keys(changes).length > 0 || deletedItems.length > 0) && (
                            <CustomButton
                                text={t("common:saveFunction")}
                                onPress={handleSave}
                                type="BUTTON_SAVE"
                                isGradient={true}
                                textTop="0"
                                sz={15}
                            />
                        )}

                        <CustomButton
                            text={t("common:cancel")}
                            onPress={handleCancelChanges}
                            type="BUTTON_CANCEL"
                            textTop="0"
                            sz={15}
                        />
                    </View>
                </View>

                <FlatList
                    data={rates}
                    keyExtractor={(item) => getRowKey(item)}
                    renderItem={({ item, index }) => renderData(item, index)}
                    refreshing={false}
                    ref={ref}
                    ListHeaderComponent={
                        <View style={common.listTitle}>
                            <View style={styles.row}>
                                <CustomLabel value={"Rate ID"} type="SUBTITLE" />
                                <CustomLabel value={t("common:description")} type="SUBTITLE" />
                                <CustomLabel value={"Default Rate"} type="SUBTITLE" />
                                <View style={{ width: 40 }} />
                            </View>
                            <View style={common.divider} />
                        </View>
                    }
                    ListEmptyComponent={
                        <CustomLabel value={"No rates found"} type="NO_FUNCTIONS" />
                    }
                    initialNumToRender={6}
                />
            </View>

            {isSaving && (
                <View style={styles.savingOverlay}>
                    <ActivityIndicator animating={true} color={colors.blue} size="large" />
                </View>
            )}
        </View>
    );
};

const makeStyles = (colors: any, width: number, height: number) =>
    StyleSheet.create({
        container: {
            flex: 1,
        },
        mainView: {
            flex: 1,
        },
        rowContainer: {
            borderRadius: 6,
        },
        row: {
            flex: 1,
            flexDirection: "row",
            alignItems: "center",
        },
        item: {
            flex: 1,
            justifyContent: "center",
            marginRight: 6,
        },
        buttonsRow: {
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginHorizontal: 15,
            marginVertical: 10,
        },
        leftButtons: {
            flexDirection: "row",
            alignItems: "center",
        },
        rightButtons: {
            flexDirection: "row",
            alignItems: "center",
        },
        loader: {
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "4%",
        },
        checkboxContainer: {
            justifyContent: "center",
            alignItems: "center",
            marginRight: 6,
            flex: 1,
        },
        actionContainer: {
            width: 50,
            alignItems: "center",
            justifyContent: "center",
        },
        changedRow: {
            backgroundColor: "rgba(255, 193, 7, 0.12)",
        },
        deletedRow: {
            backgroundColor: "rgba(220, 53, 69, 0.12)",
        },
        disabledRowContent: {
            opacity: 0.55,
        },
        savingOverlay: {
            ...StyleSheet.absoluteFillObject,
            backgroundColor: "rgba(255,255,255,0.35)",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999,
        },
    });

export default HRAdminRatesScreen